import { API } from "../api";
import {
  mapInvestorOpportunityDetail,
  mapInvestorInvestmentRequest,
} from "../../utils/investorInvestmentsAdapter";

function transformListResponse(responseData) {
  const data = responseData?.data;
  return {
    items: data?.items || [],
    pagination: data?.pagination || {},
  };
}

export const investorInvestmentsApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getInvestorPaymentFeePreview: builder.query({
      query: ({ principal_amount }) => ({
        url: `/investors/investments/payments/fee-preview`,
        method: "GET",
        params: { principal_amount },
      }),
      transformResponse: (responseData) => responseData?.data,
    }),
    listInvestorOpportunities: builder.query({
      query: (params = {}) => ({
        url: "/investors/investments/opportunities",
        method: "GET",
        params,
      }),
      transformResponse: transformListResponse,
      providesTags: [{ type: "InvestorOpportunities", id: "LIST" }],
    }),
    getInvestorOpportunity: builder.query({
      query: (slug) => ({
        url: `/investors/investments/opportunities/${slug}`,
        method: "GET",
      }),
      transformResponse: (responseData) => mapInvestorOpportunityDetail(responseData?.data),
      providesTags: (_result, _error, slug) => [{ type: "InvestorOpportunities", id: slug }],
    }),
    listInvestorInvestmentRequests: builder.query({
      query: (params = {}) => ({
        url: "/investors/investments/requests",
        method: "GET",
        params,
      }),
      transformResponse: (responseData) => {
        const data = responseData?.data;
        return {
          items: (data?.items || []).map(mapInvestorInvestmentRequest),
          pagination: data?.pagination || {},
        };
      },
      providesTags: [{ type: "InvestorInvestmentRequests", id: "LIST" }],
    }),
    getInvestorInvestmentRequest: builder.query({
      query: (requestKey) => ({
        url: `/investors/investments/requests/${encodeURIComponent(requestKey)}`,
        method: "GET",
      }),
      transformResponse: (responseData) => mapInvestorInvestmentRequest(responseData?.data),
      providesTags: (_result, _error, id) => [{ type: "InvestorInvestmentRequests", id }],
    }),
    submitInvestorInvestmentRequest: builder.mutation({
      query: ({ opportunityId, body }) => ({
        url: `/investors/investments/opportunities/${opportunityId}/requests`,
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => {
        const data = responseData?.data || {};
        return {
          ...mapInvestorInvestmentRequest(data),
          paymentUrl: data.payment_url,
          paymentReference: data.payment_reference,
          paymentProvider: data.payment_provider,
          alatpayCheckout: data.alatpay_checkout,
          feeBreakdown: data.fee_breakdown,
        };
      },
      invalidatesTags: [
        { type: "InvestorInvestmentRequests", id: "LIST" },
        { type: "InvestorOpportunities", id: "LIST" },
      ],
    }),
    cancelInvestorInvestmentRequest: builder.mutation({
      query: (requestKey) => ({
        url: `/investors/investments/requests/${encodeURIComponent(requestKey)}/cancel`,
        method: "POST",
      }),
      transformResponse: (responseData) => mapInvestorInvestmentRequest(responseData?.data),
      invalidatesTags: (_r, _e, id) => [
        { type: "InvestorInvestmentRequests", id: "LIST" },
        { type: "InvestorInvestmentRequests", id },
      ],
    }),
    /** Company support contacts — same source as Settings → Support Settings. */
    getInvestorSupportInfo: builder.query({
      query: () => ({
        url: "/investors/support",
        method: "GET",
      }),
      transformResponse: (responseData) => {
        const data = responseData?.data || {};
        return {
          phone: data.support_phone || "",
          email: data.support_email || "",
          whatsapp: data.support_whatsapp || data.support_phone || "",
        };
      },
      providesTags: [{ type: "InvestorSupport", id: "INFO" }],
    }),
    getInvestorAvailablePaymentRails: builder.query({
      query: () => ({
        url: "/investors/payments/available-rails",
        method: "GET",
      }),
      transformResponse: (responseData) => responseData?.data || { rails: [] },
      providesTags: [{ type: "InvestorPaymentRails", id: "LIST" }],
    }),
  }),
});

export const {
  useGetInvestorPaymentFeePreviewQuery,
  useListInvestorOpportunitiesQuery,
  useGetInvestorOpportunityQuery,
  useListInvestorInvestmentRequestsQuery,
  useGetInvestorInvestmentRequestQuery,
  useSubmitInvestorInvestmentRequestMutation,
  useCancelInvestorInvestmentRequestMutation,
  useGetInvestorSupportInfoQuery,
  useGetInvestorAvailablePaymentRailsQuery,
} = investorInvestmentsApi;
